import { useTranslation } from 'react-i18next';

interface TemplateContentPreviewProps {
  image?: { src: string; alt: string };
  table?: { columns: string[]; rows: string[][] };
  document?: { fileName: string; description: string; downloadHref?: string };
}

/**
 * Wizard-specific and deliberately lighter than Studio's ReferencePreview: just the content a
 * consultant needs to recognise "yes, that's the right one" — no Kind/size metadata, no
 * dependencies list. Shared by both the static (Reference/Library/Collection) and live
 * (service-sourced) preview paths, which each normalise their own data into these three shapes.
 */
export function TemplateContentPreview({ image, table, document }: TemplateContentPreviewProps) {
  const { t } = useTranslation();

  if (image) {
    return (
      <img
        src={image.src}
        alt={image.alt}
        className="mt-1 max-h-40 max-w-full rounded border border-border object-contain"
      />
    );
  }

  if (table) {
    if (table.rows.length === 0) {
      return <p className="mt-1 text-sm text-muted-foreground">{t('wizard.form.previewNoRows')}</p>;
    }

    return (
      <div className="mt-1 overflow-auto rounded border border-border">
        <table className="min-w-full divide-y divide-border text-xs">
          <thead className="bg-muted">
            <tr>
              {table.columns.map((column) => (
                <th key={column} scope="col" className="px-2 py-1 text-left font-semibold text-foreground">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {table.rows.map((row, index) => (
              // Reference/preview rows carry no id, and the list is read-only, so the index is stable.
              <tr key={index}>
                {row.map((cell, cellIndex) => (
                  <td key={table.columns[cellIndex] ?? cellIndex} className="px-2 py-1 text-muted-foreground">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (document) {
    return (
      <p className="mt-1 text-sm text-muted-foreground">
        {document.fileName || document.description || t('wizard.form.providedByTemplate')}
        {document.downloadHref ? (
          <>
            {' '}
            <a
              href={document.downloadHref}
              download={document.fileName || ''}
              className="underline hover:text-foreground"
            >
              {t('common.download')}
            </a>
          </>
        ) : null}
      </p>
    );
  }

  return null;
}
